import { useMemo } from "react";
import { Box } from "@mantine/core";
import {
  type ReportFooterProps,
  type ReportHeaderSectionProps,
} from "./ReportAttachmentPage";
import { ReportFilePage, type ReportFileAttachment } from "./ReportFilePage";
import { ReportImagePage, type ReportImageAttachment } from "./ReportImagePage";

type ReportFooterBaseProps = Omit<
  ReportFooterProps,
  "pageNumber" | "totalPages"
>;

interface ReportAttachmentPagesProps {
  reportImages: ReportImageAttachment[];
  reportFiles: ReportFileAttachment[];
  resolvedImageUrls: ReadonlyMap<string, string>;
  resolvedFileUrls: ReadonlyMap<string, string>;
  failedImageIds?: ReadonlySet<string>;
  failedFileIds?: ReadonlySet<string>;
  onImageLoadError?: (imageId: string) => void;
  onRetryImage?: (imageId: string) => void;
  onFileLoadError?: (fileId: string) => void;
  onFileLoadComplete?: (fileId: string, pageCount?: number) => void;
  onRetryFile?: (fileId: string) => void;
  allowExternalFileLinks?: boolean;
  photoReferenceWidth?: string;
  headerSectionProps?: ReportHeaderSectionProps;
  filePageCounts?: Readonly<Record<string, number>>;
  pageNumbering?: {
    footerProps: ReportFooterBaseProps;
    firstPageNumber: number;
    totalPages?: number;
  };
}

function numberedFooterProps(
  footerProps: ReportFooterBaseProps | undefined,
  pageNumber: number,
  totalPages: number
): ReportFooterProps | undefined {
  return footerProps ? { ...footerProps, pageNumber, totalPages } : undefined;
}

export function ReportAttachmentPages({
  reportImages,
  reportFiles,
  resolvedImageUrls,
  resolvedFileUrls,
  failedImageIds,
  failedFileIds,
  onImageLoadError,
  onRetryImage,
  onFileLoadError,
  onFileLoadComplete,
  onRetryFile,
  allowExternalFileLinks,
  photoReferenceWidth,
  headerSectionProps,
  filePageCounts,
  pageNumbering,
}: ReportAttachmentPagesProps) {
  const resolvedFilePageCounts = useMemo(
    () =>
      reportFiles.map((file) => Math.max(1, filePageCounts?.[file.id] ?? 1)),
    [filePageCounts, reportFiles]
  );
  const attachmentPageCount = useMemo(
    () =>
      reportImages.length +
      resolvedFilePageCounts.reduce((total, count) => total + count, 0),
    [reportImages.length, resolvedFilePageCounts]
  );
  const totalPages = useMemo(
    () =>
      pageNumbering
        ? pageNumbering.totalPages ??
          pageNumbering.firstPageNumber + attachmentPageCount - 1
        : undefined,
    [attachmentPageCount, pageNumbering]
  );
  const fileFirstPageNumbers = useMemo(() => {
    let nextPageNumber =
      (pageNumbering?.firstPageNumber ?? 1) + reportImages.length;
    return resolvedFilePageCounts.map((pageCount) => {
      const firstPageNumber = nextPageNumber;
      nextPageNumber += pageCount;
      return firstPageNumber;
    });
  }, [
    pageNumbering?.firstPageNumber,
    reportImages.length,
    resolvedFilePageCounts,
  ]);

  return (
    <Box mt={8}>
      {reportImages.map((image, imageIndex) => (
        <ReportImagePage
          key={image.id}
          image={image}
          url={resolvedImageUrls.get(image.id)}
          failed={failedImageIds?.has(image.id)}
          onLoadError={onImageLoadError}
          onRetry={onRetryImage}
          photoReferenceWidth={photoReferenceWidth}
          headerSectionProps={headerSectionProps}
          footerProps={numberedFooterProps(
            pageNumbering?.footerProps,
            (pageNumbering?.firstPageNumber ?? 1) + imageIndex,
            totalPages ?? attachmentPageCount
          )}
        />
      ))}
      {reportFiles.map((file, fileIndex) => (
        <ReportFilePage
          key={file.id}
          file={file}
          url={resolvedFileUrls.get(file.id)}
          failed={failedFileIds?.has(file.id)}
          onLoadError={onFileLoadError}
          onLoadComplete={onFileLoadComplete}
          onRetry={onRetryFile}
          allowExternalLink={allowExternalFileLinks}
          headerSectionProps={headerSectionProps}
          footerProps={pageNumbering?.footerProps}
          firstPageNumber={
            pageNumbering ? fileFirstPageNumbers[fileIndex] : undefined
          }
          totalPages={totalPages}
        />
      ))}
    </Box>
  );
}
